<template>
    <v-container>
        <template v-if="user?.is_subscribed || user?.is_admin || $page.props.free_use">
            <v-row>
                <v-col cols="6">
                    <v-select v-model="meetId" :items="clubMeets" label="Select a Meet" solo />
                </v-col>
                <v-col cols="6">
                    <v-select
                        v-model="comparisonMeetId"
                        :items="allMeets"
                        label="Select a Meet to Compare to"
                        solo
                        :loading="loadingComparison"
                    />
                </v-col>
            </v-row>

            <v-row v-if="!infoDismissed" class="mb-4">
                <v-col>
                    <v-alert type="info" dense prominent text border="left" dismissible @close="infoDismissed = true">
                        <div class="text-subtitle-1">
                            This table compares athletes' scores from the selected meet with where they would have
                            ranked in a different meet based on those scores. It provides insights into performance
                            consistency and potential standings in various competitions.
                        </div>

                        <template #close>
                            <v-btn text icon color="info" @click="infoDismissed = true">
                                <v-icon>mdi-close-circle</v-icon>
                            </v-btn>
                        </template>
                    </v-alert>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12">
                    <v-simple-table>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>TRA Div</th>
                                <th>TRA Original</th>
                                <th>TRA Comparison</th>
                                <th>DMT Div</th>
                                <th>DMT Original</th>
                                <th>DMT Comparison</th>
                                <th>TUM Div</th>
                                <th>TUM Original</th>
                                <th>TUM Comparison</th>
                            </tr>
                        </thead>

                        <tbody>
                            <tr v-for="athlete in comparisonGroupedByAthlete" :key="athlete.id">
                                <td>{{ athlete.athlete.name }}</td>
                                <td>
                                    <comparison-event
                                        v-if="athlete.TI"
                                        :comparison="athlete.TI"
                                        part="division"
                                        color="blue"
                                    />
                                </td>

                                <td>
                                    <comparison-event
                                        v-if="athlete.TI"
                                        :comparison="athlete.TI"
                                        part="original_score"
                                        color="blue"
                                    />
                                </td>

                                <td>
                                    <comparison-event
                                        v-if="athlete.TI"
                                        :comparison="athlete.TI"
                                        part="comparison"
                                        color="blue"
                                    />
                                </td>

                                <td>
                                    <comparison-event
                                        v-if="athlete.DM"
                                        :comparison="athlete.DM"
                                        part="division"
                                        color="green"
                                    />
                                </td>

                                <td>
                                    <comparison-event
                                        v-if="athlete.DM"
                                        :comparison="athlete.DM"
                                        part="original_score"
                                        color="green"
                                    />
                                </td>

                                <td>
                                    <comparison-event
                                        v-if="athlete.DM"
                                        :comparison="athlete.DM"
                                        part="comparison"
                                        color="green"
                                    />
                                </td>

                                <td>
                                    <comparison-event
                                        v-if="athlete.TU"
                                        :comparison="athlete.TU"
                                        part="division"
                                        color="red"
                                    />
                                </td>

                                <td>
                                    <comparison-event
                                        v-if="athlete.TU"
                                        :comparison="athlete.TU"
                                        part="original_score"
                                        color="red"
                                    />
                                </td>

                                <td>
                                    <comparison-event
                                        v-if="athlete.TU"
                                        :comparison="athlete.TU"
                                        part="comparison"
                                        color="red"
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-col>
            </v-row>
        </template>
        <template v-else>
            <subscription-required />
        </template>
    </v-container>
</template>

<script>
import get from 'lodash/get.js';
import Meet from '@/Models/Meet.js';
import Result from '@/Models/Result.js';
import User from '@/Models/User.js';
import SubscriptionRequired from '@/Components/SubscriptionRequired.vue';
import ComparisonEvent from '@/Components/Club/ComparisonEvent.vue';

const placementSort = (a, b) => {
    if (a.overall_placement === b.overall_placement) {
        return a.athlete.name.localeCompare(b.athlete.name);
    }

    return a.overall_placement - b.overall_placement;
};

export default {
    name: 'ClubScoreComparison',

    components: { ComparisonEvent, SubscriptionRequired },

    props: {
        clubId: {
            type: Number,
            required: true,
        },

        comparison: {
            type: [Array, Object],
            required: false,
            default: () => [],
        },
    },

    data: () => ({
        meetId: null,
        comparisonMeetId: null,
        loadingComparison: false,
        infoDismissed: false,
    }),

    computed: {
        user() {
            return User.find(this.$page.props.auth.user?.id);
        },

        clubMeets() {
            return Meet.query()
                .whereIdIn(_.map(this.$page.props.meets, 'id'))
                .get()
                .map(meet => ({
                    text: meet.title,
                    value: meet.id,
                }));
        },

        allMeets() {
            return Meet.query()
                .orderBy('starts_at', 'desc')
                .get()
                .map(meet => ({
                    text: meet.title_with_year,
                    value: meet.id,
                }));
        },

        comparisonGroupedByAthlete() {
            return this.comparison;
        },

        trampolineResults() {
            return this.eventResults('TI').sort(placementSort);
        },

        doubleMiniResults() {
            return this.eventResults('DM').sort(placementSort);
        },

        tumblingResults() {
            return this.eventResults('TU').sort(placementSort);
        },

        synchroResults() {
            return this.eventResults('TS').sort(placementSort);
        },

        meet() {
            if (!this.meetId) {
                return null;
            }

            return Meet.find(this.meetId);
        },

        hasComparison() {
            return this.meetId && this.comparisonMeetId && this.comparisonMeetId !== this.meetId;
        },

        columnWidth() {
            const competedEvents = [
                this.trampolineResults.length,
                this.doubleMiniResults.length,
                this.tumblingResults.length,
                this.synchroResults.length,
            ];

            if (_.isEmpty(competedEvents)) {
                return 12;
            }

            return 12 / _.filter(competedEvents).length;
        },
    },

    watch: {
        comparisonMeetId() {
            if (this.comparisonMeetId && this.meetId) {
                this.loadComparison();
            }
        },

        meetId() {
            if (this.comparisonMeetId && this.meetId) {
                this.loadComparison();
            }
        },
    },

    created() {
        if (this.clubMeets.length > 0) {
            this.meetId = _.get(
                Meet.query().whereIdIn(_.map(this.$page.props.meets, 'id')).orderBy('id', 'desc').first(),
                'id',
            );
        }

        const url = new URLSearchParams(window.location.search);

        if (this.comparison && url.get('comparison_meet_id')) {
            this.comparisonMeetId = Number.parseInt(url.get('comparison_meet_id'), 10);
        }
    },

    methods: {
        get,

        eventResults(event, sort = 'overall_placement', sortDirection = 'asc') {
            const results = Result.query()
                .with(['athlete', 'athlete2', 'category'])
                .where('meet_id', this.meetId)
                .where('event', event)
                .where(result => result.club_id === this.clubId || result.club_2_id === this.clubId)
                .get();

            return _.orderBy(results, [sort, sortDirection]);
        },

        totalMedals(results, placement) {
            return results.filter(
                result =>
                    result.overall_placement === String(placement) ||
                    result.overall_placement === `${placement}T` ||
                    result.overall_placement === `${placement}F`,
            ).length;
        },

        medalColor(placement) {
            return Result.medalColor(placement);
        },

        textColor(result) {
            return ['yellow darken-2', 'grey', 'brown'].includes(result.medal_color) || this.$vuetify.theme.dark
                ? 'color: #fff !important'
                : 'color: #000 !important';
        },

        loadComparison() {
            this.$inertia.reload({
                preserveState: true,
                preserveScroll: true,
                data: { meet_id: this.meetId, comparison_meet_id: this.comparisonMeetId },
                only: ['comparison'],
                onStart: () => {
                    this.loadingComparison = true;
                },
                onSuccess: () => {
                    this.loadingComparison = false;
                },
                onError: () => {
                    this.loadingComparison = false;
                },
            });
        },
    },
};
</script>
